import { InputV2, Text } from "@southfields-digital/mxmz-react-ui";
import {
	type InstanceSelection,
	isReadable,
	type OutputInstances,
	unsupplied,
} from "@southfields-digital/mxmz-resource-model";
import { useMemo } from "react";

import { usePackageResources } from "src/features/resources";
import styles from "./RundownTranslationsForm.module.scss";
import { columnsFor, offeredByLatestRelease, unrouted } from "./routing";

/** Sandbox has a rundown but no output, so it carries no id. */
type Row = { kind: "output"; id: string; name: string } | { kind: "sandbox" };

const rowKey = (row: Row) => (row.kind === "output" ? row.id : "sandbox");
const rowName = (row: Row) => (row.kind === "output" ? row.name : "Sandbox (operator preview)");

type RundownTranslationsFormProps = {
	/** Graphics package whose Resources define the available translations. */
	packageId: string | undefined;
	/** Rundown canvases — the source of which outputs are actually in play. */
	canvases: RundownCanvas[];
	outputInstances: OutputInstances;
	sandboxInstances: InstanceSelection;
	stateUpdated: (next: {
		outputInstances: OutputInstances;
		sandboxInstances: InstanceSelection;
	}) => void;
};

/** Stores only the choice: graphics-api resolves it against the pinned revisions. */
export default function RundownTranslationsForm({
	packageId,
	canvases,
	outputInstances,
	sandboxInstances,
	stateUpdated,
}: RundownTranslationsFormProps) {
	const { resources, loading, failed } = usePackageResources(packageId);

	// A single-instance translation is still routable: choosing it is what makes it air.
	// An unreadable one keeps its column so a stale selection can still be cleared.
	const routableTranslations = useMemo(
		() =>
			resources.filter(
				(translation) =>
					translation.id && (!isReadable(translation) || translation.instances.length > 0),
			),
		[resources],
	);

	// What is choosable now, which is not the revision the rundown pinned.
	const offered = useMemo(
		() => offeredByLatestRelease(routableTranslations),
		[routableTranslations],
	);

	// A selection the package no longer configures stays a column, so it can be cleared.
	const columns = useMemo(
		() =>
			columnsFor(routableTranslations, offered, [
				...Object.values(outputInstances),
				sandboxInstances,
			]),
		[routableTranslations, offered, outputInstances, sandboxInstances],
	);

	const unreadable = columns.filter((translation) => !isReadable(translation));

	// An output may back more than one canvas.
	const outputs = useMemo(() => {
		const byId = new Map<string, { id: string; name: string }>();
		for (const canvas of canvases) {
			if (canvas.output) byId.set(canvas.output.id, canvas.output);
		}
		return [...byId.values()];
	}, [canvases]);

	const rows: Row[] = [
		...outputs.map((output): Row => ({ kind: "output", ...output })),
		{ kind: "sandbox" },
	];

	const selectionFor = (row: Row): InstanceSelection =>
		row.kind === "sandbox" ? sandboxInstances : (outputInstances[row.id] ?? {});

	// Airs datafield defaults — legitimate, but only if the operator knows.
	const unroutedFor = (row: Row) => unrouted(routableTranslations, selectionFor(row));

	/** Translations whose selection this release cannot supply, whatever the reason. */
	const unsuppliedFor = (row: Row): Set<string> =>
		new Set(unsupplied(selectionFor(row), offered).map((entry) => entry.resourceId));

	const handleSelect = (row: Row, translationId: string, instance: string) => {
		const selection = { ...selectionFor(row) };

		// "-" means unrouted: an absent translation sends no data.
		if (instance) {
			selection[translationId] = instance;
		} else {
			delete selection[translationId];
		}

		stateUpdated(
			row.kind === "sandbox"
				? { outputInstances, sandboxInstances: selection }
				: { outputInstances: { ...outputInstances, [row.id]: selection }, sandboxInstances },
		);
	};

	if (loading) {
		return (
			<Text as="p" className="p-4 !text-secondary">
				Loading this package's translations…
			</Text>
		);
	}

	// Distinct from an empty list, which means the package pins nothing.
	if (failed) {
		return (
			<Text as="p" className="p-4 !text-secondary">
				This package's translations could not be read, so there is nothing safe to choose from yet.
				Try again, and check that the asset library is reachable.
			</Text>
		);
	}

	if (!columns.length) {
		return (
			<Text as="p" className="p-4 !text-secondary">
				This graphics package has nothing to translate, so there is nothing to choose.
			</Text>
		);
	}

	return (
		<>
			{!outputs.length && (
				<Text as="p" className="p-4 !text-secondary">
					No outputs are assigned to your canvases yet. Assign them to choose what each output airs
					— until then only the sandbox preview can be set.
				</Text>
			)}
			<table className={styles.TranslationsForm}>
				<thead>
					<tr>
						<th className={styles.OutputColumn}>Output</th>
						{columns.map((translation) => (
							<th key={translation.id}>
								{isReadable(translation) ? translation.name : translation.id}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{rows.map((row) => {
						const stale = unsuppliedFor(row);

						return (
							<tr key={rowKey(row)}>
								<td className={styles.OutputColumn}>{rowName(row)}</td>
								{columns.map((translation) => {
									const selected = selectionFor(row)[translation.id] ?? "";
									const known = isReadable(translation)
										? translation.instances.map(({ key, label }) => ({ label, value: key }))
										: [];
									// A selection the release no longer offers stays visible, so it can be cleared.
									const options = stale.has(translation.id)
										? [...known, { label: selected, value: selected }]
										: known;

									return (
										<td key={translation.id} className={selected ? undefined : styles.Unrouted}>
											<InputV2
												type="select"
												inputSize="lg"
												options={options}
												placeholder="-"
												selectablePlaceholder
												value={selected}
												onChange={(e) => handleSelect(row, translation.id, e.target.value)}
											/>
										</td>
									);
								})}
							</tr>
						);
					})}
				</tbody>
			</table>
			{unreadable.map((translation) =>
				isReadable(translation) ? null : (
					<Text as="p" className={styles.UnroutedNote} key={translation.id}>
						{translation.id}: {translation.error}
					</Text>
				),
			)}
			{rows.map((row) => {
				const missing = unroutedFor(row);

				if (!missing.length) return null;

				return (
					<Text as="p" className={styles.UnroutedNote} key={rowKey(row)}>
						{rowName(row)} airs field defaults for:{" "}
						{missing
							.map((translation) => (isReadable(translation) ? translation.name : translation.id))
							.join(", ")}
						.
					</Text>
				);
			})}
		</>
	);
}
